import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Plus, Package, Pencil, Trash2, ArrowLeft, Save, X, FileText, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const GestionTypesEPI = () => {
  const navigate = useNavigate();
  const [typesEPI, setTypesEPI] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    duree_validite_mois: 24,
    actif: true,
    documents_obligatoires: false,
    documents_requis: []
  });
  const [newDocument, setNewDocument] = useState({ nom: '', obligatoire: false });

  const loadTypesEPI = useCallback(async () => {
    try {
      const response = await api.get('/types-epi-specifiques');
      setTypesEPI(response.types_epi || []);
    } catch (error) {
      console.error('Error loading types EPI:', error);
      toast.error('Erreur lors du chargement des types d\'EPI');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTypesEPI();
  }, [loadTypesEPI]);

  const resetForm = () => {
    setFormData({
      nom: '',
      description: '',
      duree_validite_mois: 24,
      actif: true,
      documents_obligatoires: false,
      documents_requis: []
    });
    setNewDocument({ nom: '', obligatoire: false });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (type) => {
    setFormData({
      nom: type.nom,
      description: type.description || '',
      duree_validite_mois: type.duree_validite_mois,
      actif: type.actif,
      documents_obligatoires: type.documents_obligatoires || false,
      documents_requis: type.documents_requis || []
    });
    setEditingId(type.id);
    setShowForm(true);
  };

  const handleAddDocument = () => {
    if (!newDocument.nom.trim()) {
      toast.error('Veuillez saisir un nom de document');
      return;
    }
    
    // Vérifier si le document existe déjà
    if (formData.documents_requis.some(d => d.nom.toLowerCase() === newDocument.nom.toLowerCase())) {
      toast.error('Ce document existe déjà dans la liste');
      return;
    }

    setFormData({
      ...formData,
      documents_requis: [...formData.documents_requis, { ...newDocument, id: Date.now().toString() }]
    });
    setNewDocument({ nom: '', obligatoire: false });
  };

  const handleRemoveDocument = (docId) => {
    setFormData({
      ...formData,
      documents_requis: formData.documents_requis.filter(d => d.id !== docId)
    });
  };

  const handleToggleDocumentObligatoire = (docId) => {
    setFormData({
      ...formData,
      documents_requis: formData.documents_requis.map(d => 
        d.id === docId ? { ...d, obligatoire: !d.obligatoire } : d
      )
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nom.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }

    try {
      if (editingId) {
        // Modification
        await api.put(`/types-epi-specifiques/${editingId}`, formData);
        toast.success('Type d\'EPI modifié avec succès');
      } else {
        // Création
        await api.post('/types-epi-specifiques', formData);
        toast.success('Type d\'EPI créé avec succès');
      }

      resetForm();
      loadTypesEPI();
    } catch (error) {
      console.error('Error saving type EPI:', error);
      toast.error(error.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id, nom) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer "${nom}" ?`)) {
      return;
    }

    try {
      await api.delete(`/types-epi-specifiques/${id}`);
      toast.success('Type d\'EPI supprimé avec succès');
      loadTypesEPI();
    } catch (error) {
      console.error('Error deleting type EPI:', error);
      toast.error(error.detail || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="h-8 w-8 text-blue-600" />
            Types de demandes HSE
          </h1>
          <p className="text-gray-600 mt-2">
            Configuration des types de demandes HSE
          </p>
        </div>
        <div className="flex gap-3">
          {!showForm && (
            <Button onClick={() => setShowForm(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Nouveau type
            </Button>
          )}
          <Button onClick={() => navigate('/epi/demandes')} variant="outline" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Retour
          </Button>
        </div>
      </div>

      {/* Formulaire */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Modifier' : 'Créer'} un type de demande HSE</CardTitle>
            <CardDescription>
              Les types de demandes HSE définissent les équipements disponibles
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Informations de base */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nom">Nom *</Label>
                  <Input
                    id="nom"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    placeholder="Ex: Lunettes de sécurité sur mesure"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="duree">Durée de validité (mois) *</Label>
                  <Input
                    id="duree"
                    type="number"
                    min="1"
                    value={formData.duree_validite_mois}
                    onChange={(e) => setFormData({ ...formData, duree_validite_mois: parseInt(e.target.value) })}
                    required
                  />
                  <p className="text-xs text-gray-500">
                    Durée avant nécessité de remplacement
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description détaillée de l'équipement..."
                  rows={3}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="actif"
                  checked={formData.actif}
                  onChange={(e) => setFormData({ ...formData, actif: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <Label htmlFor="actif" className="cursor-pointer">
                  Type d'EPI actif (disponible pour les demandes)
                </Label>
              </div>

              {/* Section Documents */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  Documents à joindre
                </h3>

                <div className="space-y-4">
                  {/* Option documents obligatoires global */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <Label className="text-base font-medium">Documents requis pour ce type</Label>
                      <p className="text-sm text-gray-500">
                        Activer pour définir une liste de documents à joindre lors des demandes
                      </p>
                    </div>
                    <Switch
                      checked={formData.documents_obligatoires}
                      onCheckedChange={(checked) => setFormData({ ...formData, documents_obligatoires: checked })}
                    />
                  </div>

                  {/* Liste des documents */}
                  {formData.documents_obligatoires && (
                    <div className="space-y-4 p-4 border rounded-lg">
                      <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                        <AlertCircle className="h-4 w-4" />
                        <span>Définissez les documents requis pour ce type d'EPI. Les documents marqués "Obligatoire" devront être fournis.</span>
                      </div>

                      {/* Ajouter un document */}
                      <div className="flex gap-3 items-end">
                        <div className="flex-1">
                          <Label htmlFor="newDocNom">Nom du document</Label>
                          <Input
                            id="newDocNom"
                            value={newDocument.nom}
                            onChange={(e) => setNewDocument({ ...newDocument, nom: e.target.value })}
                            placeholder="Ex: Ordonnance médicale, Certificat d'aptitude..."
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="newDocObligatoire"
                            checked={newDocument.obligatoire}
                            onChange={(e) => setNewDocument({ ...newDocument, obligatoire: e.target.checked })}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <Label htmlFor="newDocObligatoire" className="cursor-pointer whitespace-nowrap">
                            Obligatoire
                          </Label>
                        </div>
                        <Button type="button" onClick={handleAddDocument} variant="outline" className="gap-2">
                          <Plus className="h-4 w-4" />
                          Ajouter
                        </Button>
                      </div>

                      {/* Liste des documents ajoutés */}
                      {formData.documents_requis.length > 0 && (
                        <div className="space-y-2 mt-4">
                          <Label>Documents configurés ({formData.documents_requis.length})</Label>
                          <div className="space-y-2">
                            {formData.documents_requis.map((doc) => (
                              <div key={doc.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                                <div className="flex items-center gap-3">
                                  <FileText className="h-4 w-4 text-gray-400" />
                                  <span className="font-medium">{doc.nom}</span>
                                  {doc.obligatoire ? (
                                    <Badge className="bg-red-100 text-red-800">Obligatoire</Badge>
                                  ) : (
                                    <Badge className="bg-gray-100 text-gray-600">Optionnel</Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleDocumentObligatoire(doc.id)}
                                    className="text-xs"
                                  >
                                    {doc.obligatoire ? 'Rendre optionnel' : 'Rendre obligatoire'}
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveDocument(doc.id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {formData.documents_requis.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">
                          Aucun document configuré. Ajoutez des documents ci-dessus.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button type="submit" className="gap-2">
                  <Save className="h-4 w-4" />
                  {editingId ? 'Modifier' : 'Créer'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm} className="gap-2">
                  <X className="h-4 w-4" />
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Liste */}
      <Card>
        <CardHeader>
          <CardTitle>Types d'EPI ({typesEPI.length})</CardTitle>
          <CardDescription>
            Liste des équipements de protection individuelle configurés
          </CardDescription>
        </CardHeader>
        <CardContent>
          {typesEPI.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Aucun type d'EPI configuré</p>
              <Button onClick={() => setShowForm(true)} className="mt-4 gap-2">
                <Plus className="h-4 w-4" />
                Créer le premier type
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {typesEPI.map((type) => (
                <Card key={type.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{type.nom}</h3>
                          <Badge className={type.actif ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                            {type.actif ? 'Actif' : 'Inactif'}
                          </Badge>
                          {type.documents_obligatoires && (
                            <Badge className="bg-blue-100 text-blue-800">
                              <FileText className="h-3 w-3 mr-1" />
                              Documents requis
                            </Badge>
                          )}
                        </div>
                        {type.description && (
                          <p className="text-sm text-gray-600 mb-2">{type.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>📅 Validité: {type.duree_validite_mois} mois</span>
                          {type.documents_requis && type.documents_requis.length > 0 && (
                            <span>📎 {type.documents_requis.length} document(s) configuré(s)</span>
                          )}
                          {type.date_creation && (
                            <span>• Créé le {new Date(type.date_creation).toLocaleDateString('fr-FR')}</span>
                          )}
                        </div>
                        {/* Afficher les documents requis */}
                        {type.documents_requis && type.documents_requis.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {type.documents_requis.map((doc, idx) => (
                              <Badge 
                                key={idx} 
                                variant="outline"
                                className={doc.obligatoire ? 'border-red-300 text-red-700' : 'border-gray-300 text-gray-600'}
                              >
                                {doc.nom}
                                {doc.obligatoire && ' *'}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleEdit(type)}
                          variant="outline"
                          size="sm"
                          className="gap-2"
                        >
                          <Pencil className="h-4 w-4" />
                          Modifier
                        </Button>
                        <Button
                          onClick={() => handleDelete(type.id, type.nom)}
                          variant="outline"
                          size="sm"
                          className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GestionTypesEPI;
